import { orderBy } from 'lodash-es'
import { DateTime } from 'luxon'

import CertificationRequestQ from '@/queries/CertificationRequestQ'
import CourseQ from '@/queries/CourseQ'
import {
  COURSE_CHALLENGE_TOPIC,
  COURSE_SLUG_SAT_BOOTCAMP_MATH,
  SAT_BOOTCAMP_MATH_COURSE,
  SAT_BOOTCAMP_MATH_TOPIC,
  SAT_BOOTCAMP_ONBOARDING_TOPIC,
  SAT_BOOTCAMP_READING_COURSE,
  SAT_BOOTCAMP_READING_TOPIC,
  SAT_PREP_COURSE,
  SAT_PREP_MATH_TOPIC,
  SAT_PREP_READING_TOPIC,
  SessionAttendeeRole,
} from '@/queries/constants'
import SATSeriesQ from '@/queries/SATSeriesQ'
import SATUserQ from '@/queries/SATUserQ'
import SeriesQ from '@/queries/SeriesQ'
import SessionQ from '@/queries/SessionQ'
import TopicQ from '@/queries/TopicQ'
import TrainingQ from '@/queries/TrainingQ'
import TutorProfileQ from '@/queries/TutorProfileQ'
import UserQ from '@/queries/UserQ'
import type { UserContextType } from '@/client/context/user'
import { type RootQuery, SQL } from '@/client/query'
import type { SATBootcampCertificationState } from '@/client/services/satBootcamp'
import { getSeriesTimeInfo, type SeriesTimeInfo } from '@/client/services/series'
import { SAT_BOOTCAMP_REQUIRED_TRAINING_SLUGS } from '@/client/services/training'

import { trainingFromQuery } from '@/components/Training/common'

import type { SATBootcampTutorOnboardingState } from '../../types'

export default function satBootcampTutorOnboardingStateFromQuery({
  query,
  user,
}: {
  query: RootQuery
  user: UserContextType
}): SATBootcampTutorOnboardingState {
  const me = query.one(UserQ.where({ id: user.id }))

  const tutorProfile = me.one(UserQ.getTutorProfile())
  const phoneNumber = me(UserQ.phone_number)
  const joinedSlack = tutorProfile(TutorProfileQ.getSlackEmail()) != null

  const satUser = query.one(SATUserQ.where({ userId: user.id, type: 'tutor' }))

  const eligibleMath = satUser(SATUserQ.eligible_math) ?? false
  const eligibleReading = satUser(SATUserQ.eligible_reading) ?? false
  const crosswalkId = satUser(SATUserQ.crosswalk_id)

  const parentEmail = me(UserQ.parent_email) ?? ''
  const phoneNumberWaivedAt = me(UserQ.phone_number_waived_at)
  const parentConsentCompletedAt = me(UserQ.parent_consent_completed_at)
  const parentConsentWaivedAt = me(UserQ.parent_consent_waived_at)
  const parentConsentRequestedAt = me(UserQ.parent_consent_requested_at)
  const assentCompletedAt = me(UserQ.assent_completed_at)

  const satTestIdToBootcamps: Record<
    number,
    {
      seriesId: number
      type: 'SAT Math' | 'SAT Reading'
      timeInfo: SeriesTimeInfo[]
    }[]
  > = {}
  query
    .many(
      SATSeriesQ.where(
        {
          include: [
            {
              association: 'Series',
              required: true,
              where: SQL`${SeriesQ.closed_reason} IS NULL`,
              include: [{ association: 'Course', required: true }],
            },
          ],
        },
        SQL`${SeriesQ.isHost({ userId: user.id })} AND ${SeriesQ.deleted_at} IS NULL`
      )
    )
    .forEach(satSeriesItem => {
      const satTestId = satSeriesItem(SATSeriesQ.sat_test_id) ?? 0
      const seriesId = satSeriesItem(SATSeriesQ.series_id) ?? 0
      const sessionStartDates = satSeriesItem
        .many(SeriesQ.getSessions())
        .map(session => DateTime.fromISO(`${session(SessionQ.start_date)}`))
      const timeInfo = getSeriesTimeInfo({ sessionStartDates })
      const type =
        satSeriesItem(CourseQ.slug) === COURSE_SLUG_SAT_BOOTCAMP_MATH ? 'SAT Math' : 'SAT Reading'

      satTestIdToBootcamps[satTestId] = (satTestIdToBootcamps[satTestId] ?? []).concat({
        type,
        seriesId,
        timeInfo,
      })
    })

  const unorderedTutorTrainings = query
    .many(
      TrainingQ.getTrainingSubmissions({
        userId: user.id,
        slugs: SAT_BOOTCAMP_REQUIRED_TRAINING_SLUGS,
      })
    )
    .map(item => trainingFromQuery({ training: item, submission: item }))

  const tutorTrainings = orderBy(unorderedTutorTrainings, item =>
    SAT_BOOTCAMP_REQUIRED_TRAINING_SLUGS.indexOf(item.slug)
  )

  const existingMathCertifications = query
    .many(
      CertificationRequestQ.where(
        {
          userId: user.id,
          include: [
            {
              association: 'Topic',
              required: true,
              include: [{ association: 'Course', required: true }],
            },
          ],
        },
        SQL`((${CourseQ.name} = ${SAT_BOOTCAMP_MATH_COURSE} AND ${TopicQ.name} = ${SAT_BOOTCAMP_MATH_TOPIC})
      OR (${CourseQ.name} = ${SAT_PREP_COURSE} AND ${TopicQ.name} = ${SAT_PREP_MATH_TOPIC})
      OR (${CourseQ.name} = ${SAT_PREP_COURSE} AND ${TopicQ.name} = ${COURSE_CHALLENGE_TOPIC}))`
      )
    )
    .map(certificationRequest => ({
      approvedAt: certificationRequest(CertificationRequestQ.approved_at),
    }))
  let mathCertificationState: SATBootcampCertificationState = 'none'
  if (eligibleMath) {
    mathCertificationState = 'auto'
  } else if (existingMathCertifications.length > 0) {
    if (existingMathCertifications.some(({ approvedAt }) => approvedAt != null)) {
      mathCertificationState = 'certified'
    } else {
      mathCertificationState = 'certification-pending'
    }
  }

  const existingReadingCertifications = query
    .many(
      CertificationRequestQ.where(
        {
          userId: user.id,
          include: [
            {
              association: 'Topic',
              required: true,
              include: [{ association: 'Course', required: true }],
            },
          ],
        },
        SQL`((${CourseQ.name} = ${SAT_BOOTCAMP_READING_COURSE} AND ${TopicQ.name} = ${SAT_BOOTCAMP_READING_TOPIC})
      OR (${CourseQ.name} = ${SAT_PREP_COURSE} AND ${TopicQ.name} = ${SAT_PREP_READING_TOPIC})
      OR (${CourseQ.name} = ${SAT_PREP_COURSE} AND ${TopicQ.name} = ${COURSE_CHALLENGE_TOPIC}))`
      )
    )
    .map(certificationRequest => ({
      approvedAt: certificationRequest(CertificationRequestQ.approved_at),
    }))
  let readingCertificationState: SATBootcampCertificationState = 'none'
  if (eligibleReading) {
    readingCertificationState = 'auto'
  } else if (existingReadingCertifications.length > 0) {
    if (existingReadingCertifications.some(({ approvedAt }) => approvedAt != null)) {
      readingCertificationState = 'certified'
    } else {
      readingCertificationState = 'certification-pending'
    }
  }

  const attendedOnboardingSession =
    query(
      UserQ.attendedSessionWithTopic({
        userId: user.id,
        topicName: SAT_BOOTCAMP_ONBOARDING_TOPIC,
        roles: [SessionAttendeeRole.Learner, SessionAttendeeRole.Tutor],
      })
    ) ?? false

  return {
    crosswalkId,
    parentConsentRequestedAt:
      parentConsentRequestedAt == null ? null : DateTime.fromISO(parentConsentRequestedAt),
    parentConsentCompletedAt:
      parentConsentCompletedAt == null ? null : DateTime.fromISO(parentConsentCompletedAt),
    parentConsentWaivedAt:
      parentConsentWaivedAt == null ? null : DateTime.fromISO(parentConsentWaivedAt),
    assentCompletedAt: assentCompletedAt == null ? null : DateTime.fromISO(assentCompletedAt),
    phoneNumberWaivedAt: phoneNumberWaivedAt == null ? null : DateTime.fromISO(phoneNumberWaivedAt),
    phoneNumber,
    parentEmail,
    mathCertificationState,
    readingCertificationState,
    joinedSlack,
    tutorTrainings,
    satTestIdToBootcamps,
    attendedOnboardingSession,
  }
}
